import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { 
  FaCheckCircle, FaTimes, FaEye,
  FaUser, FaBuilding, FaWeightHanging, FaMapMarkerAlt,
  FaMoneyBillWave, FaArrowLeft,
  FaFileAlt, FaExclamationTriangle, FaSave,
  FaLock, FaInfoCircle, FaCalculator
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';

function AdminJobReview({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [user, setUser] = useState({});
  
  const [pricing, setPricing] = useState({
    admin_quote_to_client: '',
    subbie_target_price: ''
  });
  const [profitMargin, setProfitMargin] = useState({ amount: 0, percentage: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/client-jobs/requests`);
      const data = await response.json();
      if (data.success) {
        setRequests(data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const clientTarget = selectedRequest?.target_price || 0;
    const adminQuote = parseFloat(pricing.admin_quote_to_client) || 0;
    const subbiePrice = parseFloat(pricing.subbie_target_price) || 0;
    
    if (adminQuote > 0 && subbiePrice > 0) {
      const profit = adminQuote - subbiePrice;
      const percentage = (profit / adminQuote) * 100;
      setProfitMargin({ amount: profit, percentage: percentage });
    } else {
      setProfitMargin({ amount: 0, percentage: 0 });
    }
  }, [pricing, selectedRequest]);

  const handlePricingChange = (field, value) => {
    setPricing(prev => ({ ...prev, [field]: value }));
  };

  const handleApprove = async (request) => {
    const clientTarget = request.target_price || 0;
    const adminQuote = parseFloat(pricing.admin_quote_to_client);
    const subbiePrice = parseFloat(pricing.subbie_target_price);
    
    if (!adminQuote || adminQuote <= 0) {
      toast.error('Please enter a valid Admin Quote to Client');
      return;
    }
    
    if (!subbiePrice || subbiePrice <= 0) {
      toast.error('Please enter a valid Subbie Target Price');
      return;
    }
    
    if (subbiePrice >= clientTarget) {
      toast.error('Subbie price must be LESS than Client Target Price');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        admin_id: user.id,
        admin_quote_to_client: adminQuote,
        subbie_target_price: subbiePrice,
        status: 'approved'
      };
      
      const response = await fetch(`${API_URL}/client-jobs/${request.id}/approve-with-pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Job request approved and converted to job!');
        fetchRequests();
        setShowModal(false);
        setPricing({ admin_quote_to_client: '', subbie_target_price: '' });
        setSelectedRequest(null);
      } else {
        toast.error(data.error || 'Failed to approve request');
      }
    } catch (err) {
      console.error('Error approving request:', err);
      toast.error('Failed to approve request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const [rejectTarget, setRejectTarget] = useState(null); // request id to reject

  const handleReject = (requestId) => {
    setRejectTarget(requestId);
  };

  const performReject = async () => {
    if (!rejectTarget) return;
    try {
      const response = await fetch(`${API_URL}/client-jobs/${rejectTarget}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('❌ Job request rejected.');
        fetchRequests();
      } else {
        toast.error(data.error || 'Failed to reject request');
      }
    } catch (err) {
      console.error('Error rejecting request:', err);
      toast.error('Failed to reject request');
    }
    setRejectTarget(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'approved': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  if (loading) {
    return (
      <AdminLayout activeTab="dashboard" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading requests...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="dashboard" onLogout={onLogout} user={user}>
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => navigate('/dashboard')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: '#0A1929' }}
        >
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>📋 Client Job Requests</h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {requests.length} pending requests
          </p>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-xl font-semibold" style={{ color: '#0A1929' }}>No Pending Requests</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>All client requests have been processed.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => (
            <div key={req.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold" style={{ color: '#0A1929' }}>{req.job_number || 'New Request'}</h3>
                    <span className={getStatusBadge(req.status)}>{req.status}</span>
                    {req.priority === 'high' && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600">
                        <FaExclamationTriangle className="inline mr-1" size={12} /> HIGH
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: '#4A5568' }}>
                    <FaBuilding className="inline mr-1" style={{ color: '#00A86B' }} />
                    {req.clients?.company_name || 'Unknown Client'}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm mt-2" style={{ color: '#4A5568' }}>
                    <span className="flex items-center gap-1">
                      <FaWeightHanging size={14} /> {req.total_quantity_mt || 0} MT
                    </span>
                    <span className="flex items-center gap-1">
                      <FaMapMarkerAlt size={14} /> {req.origin} → {req.destination}
                    </span>
                    {req.target_price && (
                      <span className="flex items-center gap-1 font-medium" style={{ color: '#00A86B' }}>
                        <FaMoneyBillWave size={14} /> Target: R{req.target_price}/MT
                      </span>
                    )}
                  </div>
                  {req.client_notes && (
                    <p className="mt-2 text-sm" style={{ color: '#4A5568' }}>
                      <span className="font-medium">Notes:</span> {req.client_notes}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button 
                    onClick={() => {
                      setSelectedRequest(req);
                      setPricing({
                        admin_quote_to_client: req.target_price || '',
                        subbie_target_price: ''
                      });
                      setShowModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg text-sm hover:bg-green transition-colors"
                  >
                    <FaCheckCircle size={14} /> Review & Approve
                  </button>
                  <button 
                    onClick={() => handleReject(req.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
                  >
                    <FaTimes size={14} /> Reject
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:border-green hover:text-green transition-colors">
                    <FaEye size={14} /> View
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold" style={{ color: '#0A1929' }}>📋 Review & Price Job</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>Job Number</p>
                    <p className="font-medium" style={{ color: '#0A1929' }}>{selectedRequest.job_number}</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>Client</p>
                    <p className="font-medium" style={{ color: '#0A1929' }}>{selectedRequest.clients?.company_name}</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>Commodity</p>
                    <p className="font-medium" style={{ color: '#0A1929' }}>{selectedRequest.commodity}</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>Quantity</p>
                    <p className="font-medium" style={{ color: '#0A1929' }}>{selectedRequest.total_quantity_mt} MT</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>Route</p>
                    <p className="font-medium" style={{ color: '#0A1929' }}>{selectedRequest.origin} → {selectedRequest.destination}</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>Client Notes</p>
                    <p className="font-medium" style={{ color: '#0A1929' }}>{selectedRequest.client_notes || 'None'}</p>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4 mb-6" style={{ borderColor: '#E8ECEF' }}>
                <h3 className="font-bold mb-4 flex items-center gap-2" style={{ color: '#0A1929' }}>
                  <FaMoneyBillWave style={{ color: '#00A86B' }} /> Pricing
                </h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                    Client Target Price <span className="text-xs text-gray-400">(Client's request - LOCKED)</span>
                  </label>
                  <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg border" style={{ borderColor: '#E8ECEF' }}>
                    <span className="font-bold" style={{ color: '#0A1929' }}>R</span>
                    <span className="font-bold text-lg" style={{ color: '#0A1929' }}>
                      {selectedRequest.target_price || 'Not Provided'}
                    </span>
                    <span className="text-sm" style={{ color: '#94A3B8' }}>/MT</span>
                    <FaLock className="ml-auto" style={{ color: '#94A3B8' }} />
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                    <FaInfoCircle className="inline mr-1" size={12} /> This is the client's suggested price. Your quote can be higher or lower.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                    Admin Quote to Client <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="font-bold" style={{ color: '#0A1929' }}>R</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricing.admin_quote_to_client}
                      onChange={(e) => handlePricingChange('admin_quote_to_client', e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                      style={{ borderColor: '#E8ECEF' }}
                      placeholder="Enter quote for client"
                      required
                    />
                    <span className="text-sm" style={{ color: '#94A3B8' }}>/MT</span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                    <FaInfoCircle className="inline mr-1" size={12} /> This is the price you will quote to the client.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                    Subbie Target Price <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="font-bold" style={{ color: '#0A1929' }}>R</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricing.subbie_target_price}
                      onChange={(e) => handlePricingChange('subbie_target_price', e.target.value)}
                      className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-green ${
                        pricing.subbie_target_price && selectedRequest.target_price && 
                        parseFloat(pricing.subbie_target_price) >= parseFloat(selectedRequest.target_price)
                          ? 'border-red-500 bg-red-50'
                          : ''
                      }`}
                      style={{ borderColor: '#E8ECEF' }}
                      placeholder="Enter price for subbie"
                      required
                    />
                    <span className="text-sm" style={{ color: '#94A3B8' }}>/MT</span>
                  </div>
                  {selectedRequest.target_price && pricing.subbie_target_price && (
                    <div className="mt-1">
                      {parseFloat(pricing.subbie_target_price) >= parseFloat(selectedRequest.target_price) ? (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <FaExclamationTriangle size={12} /> 
                          Subbie price must be LESS than Client Target Price
                        </p>
                      ) : (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <FaCheckCircle size={12} /> 
                          Valid: Subbie price is less than Client Target Price
                        </p>
                      )}
                    </div>
                  )}
                  <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                    <FaInfoCircle className="inline mr-1" size={12} /> This is the price you will offer to the subcontractor.
                  </p>
                </div>

                {pricing.admin_quote_to_client && pricing.subbie_target_price && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-medium flex items-center gap-2" style={{ color: '#0A1929' }}>
                      <FaCalculator style={{ color: '#00A86B' }} /> Profit Margin (Auto-Calculated)
                    </h4>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <p className="text-xs" style={{ color: '#94A3B8' }}>Profit per MT</p>
                        <p className="text-lg font-bold" style={{ color: '#00A86B' }}>R{profitMargin.amount.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: '#94A3B8' }}>Profit Percentage</p>
                        <p className="text-lg font-bold" style={{ color: '#00A86B' }}>{profitMargin.percentage.toFixed(2)}%</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs" style={{ color: '#94A3B8' }}>Total Profit</p>
                        <p className="text-lg font-bold" style={{ color: '#00A86B' }}>
                          R{(profitMargin.amount * (selectedRequest.total_quantity_mt || 0)).toFixed(2)}
                        </p>
                        <p className="text-xs" style={{ color: '#94A3B8' }}>
                          {selectedRequest.total_quantity_mt || 0} MT × R{profitMargin.amount.toFixed(2)} = R{(profitMargin.amount * (selectedRequest.total_quantity_mt || 0)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border rounded-lg text-sm font-medium transition-colors"
                  style={{ backgroundColor: '#F8F9FA', color: '#4A5568', borderColor: '#E8ECEF' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleApprove(selectedRequest)}
                  disabled={isSubmitting}
                  className="px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#00A86B' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                >
                  <FaSave size={14} />
                  {isSubmitting ? 'Processing...' : '✅ Approve & Convert to Job'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title="Reject Request"
        message="Are you sure you want to reject this request?"
        confirmLabel="Reject"
        danger
        onConfirm={performReject}
      />
    </AdminLayout>
  );
}

export default AdminJobReview;
